import { useEffect, useMemo, useRef, useState } from "react";
import { CartConfigInterface, CartData, CartLineItem } from "../CartTypes";
import styles from "./CartDrawer.module.css";
import "react-step-progress-bar/styles.css";
import { ATTRIBUTE_KEY_SHORTCODES, updateRewardFreeGifts } from "../../Bundles/constants";
import { AddProductToCartBox } from "./CartFooter";
import Button from "../../Bundles/BundlesComponents/Button";
import Spinner from "../../../components/Icons/Spinner";
import { addBundleToCart } from "../../Bundles/RenderOnStore/utilities";
import { ProductObj, VariantsObj } from "../../Bundles/Types";

type QualifiedRewardEntry = {
    tierIndex: number;
    rewardIndex: number;
    product: ProductObj;
};

type RemovedRewardMap = {
    [rewardKey: string]: true;
};

const getRewardKey = (tierIndex: number, rewardIndex: number) => `${tierIndex}_${rewardIndex}`;

const TriggeredRewards = ({
    cart,
    cartConfig,
    standAlone,
    sft,
    currency,
    cartFreeGiftItems,
}: {
    cart: CartData;
    cartConfig: CartConfigInterface;
    standAlone: boolean;
    sft: string;
    currency: string;
    cartFreeGiftItems: CartLineItem[];
}) => {
    const tiers = cartConfig.triggered_rewards.tiers || [];

    const processingRewardRef = useRef<Set<string>>(new Set());

    const [removedRewardMap, setRemovedRewardMap] = useState<RemovedRewardMap>(() =>
        JSON.parse(sessionStorage.getItem("removedFreeGifts") || "{}"),
    );

    const restoreRemovedReward = (reward) => {
        const key = getRewardKey(reward.tierIndex, reward.rewardIndex);

        setRemovedRewardMap((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    useEffect(() => {
        sessionStorage.setItem("removedFreeGifts", JSON.stringify(removedRewardMap));
    }, [removedRewardMap]);

    const [qualifiedRewards, setQualifiedRewards] = useState<QualifiedRewardEntry[]>([]);

    const checkIfCartQualifies = () => {
        // Track product IDs that have already triggered a reward to prevent double-dipping.
        // A product can only contribute to one reward tier.
        const usedProductIds = new Set<string>();
        const qualified: QualifiedRewardEntry[] = [];

        tiers.forEach((tier, tierIndex: number) => {
            if (tier.trigger_type === "product") {
                const tierProductIds = (tier.products || []).map((p: any) => p.id);

                // Find items in cart that match this tier AND haven't been used yet
                const matchingItems = cart.items.filter((item) => {
                    const shopifyPId = `gid://shopify/Product/${item.product_id}`;
                    return tierProductIds.includes(shopifyPId) && !usedProductIds.has(shopifyPId);
                });

                if (matchingItems.length > 0) {
                    // Mark matching products as used
                    matchingItems.forEach((item) => {
                        usedProductIds.add(`gid://shopify/Product/${item.product_id}`);
                    });

                    tier.free_gifts.forEach((gift: ProductObj, rewardIndex: number) => {
                        qualified.push({
                            tierIndex,
                            rewardIndex,
                            product: gift,
                        });
                    });
                }
            } else if (tier.trigger_type === "collection") {
                const tierCollectionIds = (tier.collections || []).map((c: any) => c.id);

                // Find items in cart that match this collection AND haven't been used yet
                const matchingItems = cart.items.filter((item) => {
                    const shopifyPId = `gid://shopify/Product/${item.product_id}`;
                    const hasCollection = (item?.collections || []).some((id: string) =>
                        tierCollectionIds.includes(id),
                    );
                    return hasCollection && !usedProductIds.has(shopifyPId);
                });

                if (matchingItems.length > 0) {
                    // Mark matching products as used
                    matchingItems.forEach((item) => {
                        usedProductIds.add(`gid://shopify/Product/${item.product_id}`);
                    });

                    tier.free_gifts.forEach((gift: ProductObj, rewardIndex: number) => {
                        qualified.push({
                            tierIndex,
                            rewardIndex,
                            product: gift,
                        });
                    });
                }
            }
        });

        return qualified;
    };

    const getRemovedMap = (): RemovedRewardMap => JSON.parse(sessionStorage.getItem("removedFreeGifts") || "{}");

    const setRemovedMap = (map: RemovedRewardMap) => sessionStorage.setItem("removedFreeGifts", JSON.stringify(map));

    useEffect(() => {
        let cancelled = false;

        const reconcile = async () => {
            if (cancelled) return;
            const qualified = checkIfCartQualifies();

            // hydrate products
            let hydratedRewards = qualified;
            if (qualified.length) {
                const products = qualified.map((r) => r.product);
                const updated = await updateRewardFreeGifts(products, sft, standAlone);
                hydratedRewards = qualified.map((r, i) => ({
                    ...r,
                    product: updated[i],
                }));
            }
            setQualifiedRewards(hydratedRewards);

            const removedMap = getRemovedMap();
            const cartFreeGifts = cartFreeGiftItems;

            const qualifiedKeys = new Set(hydratedRewards.map((r) => getRewardKey(r.tierIndex, r.rewardIndex)));

            /* ---------------- AUTO REMOVE ---------------- */
            for (const item of cartFreeGifts) {
                const rewardKey =
                    item.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[ATTRIBUTE_KEY_SHORTCODES.__triggerRewards]
                        ?.rewardKey;

                if (!rewardKey) continue;
                if (processingRewardRef.current.has(rewardKey)) continue;
                if (qualifiedKeys.has(rewardKey)) continue;

                await fetch("/cart/change.js", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ id: item.key, quantity: 0 }),
                });

                delete removedMap[rewardKey];
            }

            /* ---------------- AUTO ADD ---------------- */
            for (const reward of hydratedRewards) {
                const rewardKey = getRewardKey(reward.tierIndex, reward.rewardIndex);

                if (processingRewardRef.current.has(rewardKey)) continue;
                if (removedMap[rewardKey]) continue;

                const alreadyInCart = cartFreeGifts.some(
                    (item) =>
                        item.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[ATTRIBUTE_KEY_SHORTCODES.__triggerRewards]
                            ?.rewardKey === rewardKey,
                );

                if (alreadyInCart) continue;

                // Check for available variant to avoid infinite loops if product is out of stock
                const availableVariant = reward.product?.variants?.find((v: VariantsObj) => v.availableForSale);
                if (!availableVariant) {
                    console._log(`Skipping auto-add for ${reward.product.title} as no variants are available.`);
                    continue;
                }

                processingRewardRef.current.add(rewardKey);

                await addBundleToCart(
                    [
                        {
                            ...reward.product,
                            selectedVariants: Array(reward.product.quantity || 1).fill(availableVariant),
                        },
                    ],
                    {
                        [ATTRIBUTE_KEY_SHORTCODES.__triggerRewards]: { rewardKey },
                    },
                    "",
                    false,
                    "",
                );

                processingRewardRef.current.delete(rewardKey);
            }

            // setRemovedMap(removedMap);
            setRemovedRewardMap((prev) => {
                // only update if changed (prevents loops)
                const prevStr = JSON.stringify(prev);
                const nextStr = JSON.stringify(removedMap);
                if (prevStr === nextStr) return prev;
                return removedMap;
            });
        };

        reconcile();
        return () => {
            cancelled = true;
        };
    }, [cart]);

    const [addProductToCart, setAddProductToCart] = useState(null);
    const [curRewardIndexes, setCurRewardIndexes] = useState<any>(null);
    const [addingGiftMap, setAddingGiftMap] = useState<{ [productId: string]: boolean }>({});
    const setAddingGift = (rewardKey: string, isLoading: boolean) => {
        setAddingGiftMap((prev) => ({ ...prev, [rewardKey]: isLoading }));
    };

    const removedQualifiedRewards = useMemo(() => {
        return qualifiedRewards.filter((r) => removedRewardMap[getRewardKey(r.tierIndex, r.rewardIndex)]);
    }, [qualifiedRewards, removedRewardMap, cart]);

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                flexDirection: "column",
            }}
        >
            {removedQualifiedRewards && removedQualifiedRewards?.length > 0 && (
                <div
                    style={{
                        marginTop: 10,
                        width: "92%",
                    }}
                >
                    {cartConfig?.reward_free_gift_text && (
                        <p
                            style={{
                                fontSize: "15px",
                                marginBottom: "6px",
                                textAlign: "left",
                                color: cartConfig?.reward_design?.reward_free_gift_text_color,
                            }}
                        >
                            {cartConfig?.reward_free_gift_text}
                        </p>
                    )}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {removedQualifiedRewards?.map((reward) => {
                            const product = reward.product;
                            const variantId = product.id.split("/").pop();
                            const isInCart = cart.items.some(
                                (item: any) => item.variant_id.toString() === variantId.toString(),
                            );
                            const rewardKey = getRewardKey(reward.tierIndex, reward.rewardIndex);
                            const isAddingThisGift = addingGiftMap[rewardKey] || false;

                            return !isInCart ? (
                                <div
                                    key={variantId}
                                    style={{
                                        display: "flex",
                                        gap: "10px",
                                        alignItems: "center",
                                        padding: "6px 10px",
                                        background: cartConfig?.reward_design?.free_gift_bg_color,
                                        border: "1px solid #ccc",
                                        borderRadius: 6,
                                        position: "relative",
                                    }}
                                >
                                    <img
                                        src={product?.images?.[0]?.originalSrc}
                                        alt=""
                                        style={{
                                            width: "45px",
                                            height: "45px",
                                            minWidth: "45px",
                                            minHeight: "45px",
                                            borderRadius: "5px",
                                            objectFit: "cover",
                                        }}
                                    />
                                    <img
                                        src="https://storage.scalenut.com/prod/user-upload-images/624d2ea5-105d-481b-b80f-06628b8829a3.png"
                                        alt=""
                                        style={{
                                            width: "30px",
                                            height: "30px",
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                        }}
                                    />
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            gap: "10px",
                                            alignItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                gap: "6px",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    color: cartConfig?.reward_design
                                                        ?.reward_free_gift_product_title_color,
                                                }}
                                            >
                                                {product.title}
                                            </span>

                                            <span
                                                style={{
                                                    color: "green",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                FREE
                                            </span>
                                        </div>

                                        <Button
                                            title={isAddingThisGift ? <Spinner size={20} /> : "Add"}
                                            disabledCta={isAddingThisGift}
                                            onClick={async (e) => {
                                                e?.preventDefault();
                                                e?.stopPropagation();

                                                if (standAlone) {
                                                    if (product?.variants?.length > 1) {
                                                        setCurRewardIndexes({
                                                            tierIndex: reward.tierIndex,
                                                            rewardIndex: reward.rewardIndex,
                                                        });
                                                        setAddProductToCart({
                                                            ...product,
                                                            selectedVariants: Array(product.quantity || 1).fill(
                                                                product.variants.find(
                                                                    (v: any) => v?.availableForSale == true,
                                                                ) || product.variants[0],
                                                            ),
                                                        });
                                                    } else {
                                                        try {
                                                            const rewardKey = getRewardKey(
                                                                reward.tierIndex,
                                                                reward.rewardIndex,
                                                            );
                                                            setAddingGift(rewardKey, true);

                                                            processingRewardRef.current.add(rewardKey);
                                                            await addBundleToCart(
                                                                [
                                                                    {
                                                                        ...product,
                                                                        selectedVariants: Array(
                                                                            product?.quantity || 1,
                                                                        ).fill(product.variants[0]),
                                                                    },
                                                                ],
                                                                {
                                                                    [ATTRIBUTE_KEY_SHORTCODES.__triggerRewards]: {
                                                                        rewardKey,
                                                                    },
                                                                },
                                                                "",
                                                                false,
                                                                "",
                                                            );
                                                            restoreRemovedReward(reward);
                                                            processingRewardRef.current.delete(rewardKey);
                                                        } catch (error) {
                                                            console._log("Free gift not added: ", error);
                                                        } finally {
                                                            setAddingGift(rewardKey, false);
                                                        }
                                                    }
                                                }
                                            }}
                                            style={{
                                                padding: "6px 16px",
                                                backgroundColor: cartConfig?.reward_design?.free_gift_button_bg_color,
                                                color: cartConfig?.reward_design?.free_gift_button_text_color,
                                                border: "none",
                                                borderRadius: cartConfig?.upsell_design?.upsell_button_radius,
                                                cursor: "pointer",
                                                fontSize: 12,
                                                minHeight: "max-content",
                                                maxHeight: "max-content",
                                                fontWeight: "400",
                                                minWidth: "60px",
                                                maxWidth: "max-content",
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : null;
                        })}
                    </div>

                    {/* @ts-expect-error hack */}
                    {addProductToCart?.id && (
                        <AddProductToCartBox
                            setVariant={setAddProductToCart}
                            product={addProductToCart}
                            cartConfig={cartConfig}
                            currency={currency}
                            money_format={""}
                            addToCartFromCart={addBundleToCart}
                            cart={cart}
                            standAlone={standAlone}
                            isRewardFreeGift={true}
                            propertyName={ATTRIBUTE_KEY_SHORTCODES.__triggerRewards}
                            triggerRewardExtraFxn={() => {
                                const rewardKey = getRewardKey(
                                    curRewardIndexes.tierIndex,
                                    curRewardIndexes.rewardIndex,
                                );
                                processingRewardRef.current.add(rewardKey);
                            }}
                            triggerRewardExtraFxn2={() => {
                                const rewardKey = getRewardKey(
                                    curRewardIndexes.tierIndex,
                                    curRewardIndexes.rewardIndex,
                                );
                                processingRewardRef.current.delete(rewardKey);
                                setAddProductToCart(null);
                                restoreRemovedReward(curRewardIndexes);
                            }}
                            rewardKey={getRewardKey(curRewardIndexes.tierIndex, curRewardIndexes.rewardIndex)}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default TriggeredRewards;
